import {
  ButtonDS,
  DeleteOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  Switch,
  TextCapitalized,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  companyFollowUpAsync,
  companyFollowUpSlice,
  createCompanyExternalIbanAsync,
  deleteCompanyAsync,
  selectCompanyFollowUp,
  selectCompanyFollowUpStatus,
  selectCreateCompanyAcronymStatus,
  selectCreateCompanyExternalIbanStatus,
  selectDeleteCompanyStatus,
  selectUpdateCompanyHasGacStatus,
  selectUpdateCompanyHasGeolocationStatus,
  updateCompanyHasGacAsync,
  updateCompanyHasGeolocationAsync,
} from "./followUpSlice";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { CompanyFollowUp, CompanyFollowUpDisplayed } from "./followUpApi";
import { useTranslation } from "react-i18next";
import {
  ExternalIbanModal,
  ExternalIbanModalState,
} from "../modals/externalIbanModal";
import { ExternalIbanInput } from "./followUpInputs/externalIbanInput";
import { AcronymInput } from "./followUpInputs/acronymInput";
import "../../i18n/i18n";
import { selectAdminUser } from "../homeSlice";
import {
  DeleteCompanyModal,
  DeleteCompanyModalState,
} from "./modals/deleteCompanyModal";

export const CompanyFollowUpTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const params = useParams();
  const companyFollowUpList = useAppSelector(selectCompanyFollowUp);
  const companyFollowUpListStatus = useAppSelector(selectCompanyFollowUpStatus);
  const createCompanyAcronymStatus = useAppSelector(
    selectCreateCompanyAcronymStatus,
  );
  const createCompanyExternalIbanStatus = useAppSelector(
    selectCreateCompanyExternalIbanStatus,
  );
  const deleteCompanyStatus = useAppSelector(selectDeleteCompanyStatus);

  const [listDisplayed, setListDisplayed] = useState<
    CompanyFollowUpDisplayed[] | null
  >(null);
  const adminUser = useAppSelector(selectAdminUser);
  const updateCompanyHasGacStatus = useAppSelector(
    selectUpdateCompanyHasGacStatus,
  );
  const updateCompanyHasGeolocationStatus = useAppSelector(
    selectUpdateCompanyHasGeolocationStatus,
  );

  const [modalState, setModalState] = useState<ExternalIbanModalState>({
    isVisible: false,
  });
  const [deleteCompanyModalState, setDeleteCompanyModalState] =
    useState<DeleteCompanyModalState>({
      isVisible: false,
    });

  const handleModalVisibility = useCallback(
    (visible: boolean, iban?: string, companyUuid?: string, name?: string) => {
      setModalState({
        isVisible: visible,
        ...(visible ? { iban, companyUuid, name } : {}),
      });
    },
    [],
  );

  const handleDeleteCompanyModalVisibility = useCallback(
    (visible: boolean, companyUuid?: string, name?: string) => {
      setDeleteCompanyModalState({
        isVisible: visible,
        ...(visible ? { companyUuid, name } : {}),
      });
    },
    [],
  );

  const handleConfirmDeleteCompany = useCallback(() => {
    setDeleteCompanyModalState({
      isVisible: false,
    });
    if (deleteCompanyModalState.companyUuid && adminUser?.email) {
      void dispatch(
        deleteCompanyAsync({
          companyUuid: deleteCompanyModalState.companyUuid,
          deleteBy: adminUser.email,
        }),
      );
    }
  }, [adminUser?.email, deleteCompanyModalState, dispatch]);

  const handleConfirmExternalIban = useCallback(() => {
    setModalState({
      isVisible: false,
    });
    if (modalState.companyUuid && modalState.iban) {
      void dispatch(
        createCompanyExternalIbanAsync({
          companyUuid: modalState.companyUuid,
          externalIban: modalState.iban,
        }),
      );
    }
  }, [modalState, dispatch]);

  const authorizedAdmin = useMemo(() => {
    return [
      "a.geraud@qivia.fr",
      "p.chopin@qivia.fr",
      "t.rossi@qivia.fr",
      "g.leroy@qivia.fr",
      "m.gatinois@qivia.fr",
      "m.banjac@qivia.fr",
    ].includes(adminUser?.email ?? "");
  }, [adminUser]);

  const pageName = "companyFollowUp";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      amount: "avg",
      merchantName: "size",
      category: "size",
      driverName: "size",
    },
  };

  const findKeysToTranslate = useCallback(
    (key: keyof CompanyFollowUp, value: string) => {
      switch (key) {
        case "status":
          return t(`companyFollowUp.status.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () => ["status"] as (keyof CompanyFollowUpDisplayed)[],
    [],
  );

  const companyListWithId = useMemo(() => {
    const rowsWithId = companyFollowUpList.map((cd) => {
      let newDict: CompanyFollowUp & { id: string } = {
        ...cd,
        id: cd.companyUuid,
      };
      Object.entries(cd).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof CompanyFollowUpDisplayed)) {
          newDict = {
            ...newDict,
            [key]: findKeysToTranslate(
              key as keyof CompanyFollowUp,
              value as string,
            ),
          };
        }
      });
      return newDict;
    });
    return rowsWithId;
  }, [companyFollowUpList, findKeysToTranslate, keysToTranslate]);

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const searchBarProps = {
    values: companyListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const isAcronymValid = useCallback(
    (input: string) =>
      companyFollowUpList.reduce(
        (acc, curr) => acc && curr.acronym !== input,
        true,
      ),
    [companyFollowUpList],
  );

  useEffect(() => {
    if (createCompanyAcronymStatus === "success") {
      triggerToast(t("companyFollowUp.acronym.create.success") || "", "valid");
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (createCompanyAcronymStatus === "failed") {
      triggerToast(t("companyFollowUp.acronym.create.failure") || "", "error");
    }
  }, [createCompanyAcronymStatus, t, dispatch]);

  useEffect(() => {
    if (createCompanyExternalIbanStatus === "success") {
      triggerToast(
        t("companyFollowUp.externalIban.create.success") || "",
        "valid",
      );
      dispatch(
        companyFollowUpSlice.actions.resetCreateCompanyExternalIbanStatus(),
      );
    } else if (createCompanyExternalIbanStatus === "failed") {
      triggerToast(
        t("companyFollowUp.externalIban.create.failure") || "",
        "error",
      );
    }
  }, [createCompanyExternalIbanStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHasGacStatus === "success") {
      triggerToast(t("companyFollowUp.hasGac.update.success") || "", "valid");
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyHasGacStatus === "failed") {
      triggerToast(t("companyFollowUp.hasGac.update.failure") || "", "error");
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyHasGacStatus, t, dispatch]);

  useEffect(() => {
    if (updateCompanyHasGeolocationStatus === "success") {
      triggerToast(
        t("companyFollowUp.hasGeolocation.update.success") || "",
        "valid",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    } else if (updateCompanyHasGeolocationStatus === "failed") {
      triggerToast(
        t("companyFollowUp.hasGeolocation.update.failure") || "",
        "error",
      );
      dispatch(companyFollowUpSlice.actions.resetUpdateStatus());
    }
  }, [updateCompanyHasGeolocationStatus, t, dispatch]);

  useEffect(() => {
    if (deleteCompanyStatus === "success") {
      triggerToast(t("companyFollowUp.delete.success") || "", "valid");
      dispatch(companyFollowUpSlice.actions.resetDeleteCompanyStatus());
      dispatch(companyFollowUpSlice.actions.resetCompanyFollowUpStatus());
    } else if (deleteCompanyStatus === "failed") {
      triggerToast(t("companyFollowUp.delete.failure") || "", "error");
      dispatch(companyFollowUpSlice.actions.resetDeleteCompanyStatus());
    }
  }, [deleteCompanyStatus, t, dispatch]);

  useEffect(() => {
    if (companyFollowUpListStatus === "idle") {
      void dispatch(companyFollowUpAsync());
    }
  }, [companyFollowUpListStatus, dispatch]);

  const columnsDisplayed: {
    [index in keyof CompanyFollowUpDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      name: {
        type: "string",
        width: 200,
      },
      balance: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) => {
          return value ? <>{Math.round(value / 100)} €</> : <>N/A</>;
        },
      },
      totalCardsCount: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) => {
          return value ? <>{value}</> : <>N/A</>;
        },
      },
      activeCardsCount: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) => {
          return value ? <>{value}</> : <>N/A</>;
        },
      },
      inOppositionCardsCount: {
        type: "number",
        width: 200,
        valueFormatter: (value: number) => {
          return value ? <>{value}</> : <>N/A</>;
        },
      },
      acronym: {
        type: "string",
        width: 300,
        renderCell: (props) => {
          if (props.value) return <>{props.value}</>;
          return (
            <AcronymInput
              isAcronymValid={isAcronymValid}
              companyUuid={props.row["companyUuid"] as string}
            />
          );
        },
      },
      externalIban: {
        type: "string",
        width: 360,
        renderCell: (props) => {
          if (props.value) return <>{props.value}</>;
          return (
            <ExternalIbanInput
              setIsExternalIbanModalVisible={(
                visible: boolean,
                iban?: string,
              ) =>
                handleModalVisibility(
                  visible,
                  iban,
                  props.row["companyUuid"] as string,
                  props.row["name"] as string,
                )
              }
            />
          );
        },
      },
      hasGac: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <HasGacCellContainer>
              <Switch
                checked={props.row["hasGac"] as boolean}
                size={"S"}
                onClick={() => {
                  void dispatch(
                    updateCompanyHasGacAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasGac: !(props.row["hasGac"] as boolean),
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </HasGacCellContainer>
          );
        },
      },
      hasGeolocation: {
        type: "string",
        width: 200,
        renderCell: (props) => {
          return (
            <HasGeolocationCellContainer>
              <Switch
                checked={props.row["hasGeolocation"] as boolean}
                size={"S"}
                onClick={() => {
                  void dispatch(
                    updateCompanyHasGeolocationAsync({
                      companyUuid: props.row["companyUuid"] as string,
                      hasGeolocation: !(props.row["hasGeolocation"] as boolean),
                      updatedBy: adminUser?.email ?? "",
                    }),
                  );
                }}
              />
            </HasGeolocationCellContainer>
          );
        },
      },
      status: {
        type: "string",
        width: 200,
        valueFormatter: (value: string) => {
          return <TextCapitalized>{value}</TextCapitalized>;
        },
      },
      deleteCompany: {
        type: "string",
        width: 220,
        valueFormatter: (
          value: string | null,
          row: CompanyFollowUpDisplayed & { id: string },
        ) => {
          if (row["status"] === t("companyFollowUp.status.DELETE")) {
            return (
              <TextCapitalized>
                {t("companyFollowUp.status.alreadyDeleted")}
              </TextCapitalized>
            );
          }
          return (
            <StyledColumnButton>
              <ButtonDS
                singleIcon={{
                  icon: <DeleteOutlined />,
                  size: "L",
                }}
                buttonType="danger"
                format={"fill"}
                onClick={() => {
                  if (!authorizedAdmin) {
                    return;
                  }
                  handleDeleteCompanyModalVisibility(
                    true,
                    row["id"],
                    row["name"],
                  );
                }}
                disabled={!authorizedAdmin}
              />
            </StyledColumnButton>
          );
        },
      },
    };
  }, [
    isAcronymValid,
    handleModalVisibility,
    dispatch,
    adminUser?.email,
    authorizedAdmin,
    handleDeleteCompanyModalVisibility,
    t,
  ]);

  useEffect(() => {
    if (companyListWithId && companyFollowUpListStatus === "success") {
      setListDisplayed(companyListWithId);
    }
  }, [companyListWithId, companyFollowUpListStatus]);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (
    params.tab !== "companyFollowUp" ||
    adminUser?.email === "exterieur1@qivia.fr"
  ) {
    return;
  }

  return (
    <StyledContainer>
      <Spacer y={2} />
      <ExternalIbanModal
        visible={modalState.isVisible}
        onClose={() => setModalState({ isVisible: false })}
        selectedIban={modalState.iban}
        selectedName={modalState.name}
        onConfirm={handleConfirmExternalIban}
        modalType="ADD"
      />
      <MaterialUiTable<keyof CompanyFollowUpDisplayed, CompanyFollowUpDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
      <DeleteCompanyModal
        visible={deleteCompanyModalState.isVisible}
        onClose={() => setDeleteCompanyModalState({ isVisible: false })}
        selectedCompanyName={deleteCompanyModalState.name}
        onConfirm={handleConfirmDeleteCompany}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HasGacCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const HasGeolocationCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
